import { useApolloClient } from "@apollo/client";
import { useCallback, useRef, useState } from "react";
import { TraceProductCertificates } from "../graphql/queries";
import { Certificate, ProductCertificatesMap, EventProductInstance } from "../types";

export function useProductCertificates() {
    const client = useApolloClient();
    const [productCertificates, setProductCertificates] = useState<ProductCertificatesMap>({});
    const [loading, setLoading] = useState<boolean>(false);
    const fetchedEventIds = useRef<Set<string>>(new Set());
    const pendingEventIds = useRef<Set<string>>(new Set());

    const fetchProductCertificates = useCallback(async (eventProdInstances: EventProductInstance[]) => {
        const newInstances = eventProdInstances.filter(
            item => !fetchedEventIds.current.has(item.eventId) && !pendingEventIds.current.has(item.eventId)
        );

        if (newInstances.length === 0) return;

        newInstances.forEach(item => pendingEventIds.current.add(item.eventId));
        setLoading(true);

        const maxRetries = 2;
        let attempt = 0;
        let success = false;

        while (attempt < maxRetries && !success) {
            try {
                const { data } = await client.query({
                    query: TraceProductCertificates,
                    variables: {
                        input: {
                            eventProdInstancesInput: newInstances,
                            isAuth: false
                        }
                    },
                    fetchPolicy: "network-only",
                });

                const productCertInfo = data?.traceProductCertificates?.productCertInfo || [];

                const newCertificates: ProductCertificatesMap = {};

                productCertInfo.forEach((info: { eventId: string; instanceId: string; productCertificates: Certificate[] }) => {
                    if (!newCertificates[info.eventId]) {
                        newCertificates[info.eventId] = [];
                    }
                    // Store each instance's certificates separately
                    newCertificates[info.eventId]?.push({
                        instanceId: info.instanceId,
                        productCertificates: info.productCertificates || []
                    });
                    fetchedEventIds.current.add(info.eventId);
                });

                newInstances.forEach(item => {
                    if (!newCertificates.hasOwnProperty(item.eventId)) {
                        newCertificates[item.eventId] = [];
                        fetchedEventIds.current.add(item.eventId);
                    }
                });

                setProductCertificates(prev => ({
                    ...prev,
                    ...newCertificates
                }));

                success = true;
            } catch (error) {
                attempt++;
                if (attempt >= maxRetries) {
                    newInstances.forEach(item => {
                        fetchedEventIds.current.add(item.eventId);
                    });
                }
            }
        }

        newInstances.forEach(item => pendingEventIds.current.delete(item.eventId));
        setLoading(false);
    }, [client]);

    return {
        productCertificates,
        loading,
        fetchProductCertificates
    };
}
